/* hs-eslint ignored failing-rules */

/* eslint-disable no-prototype-builtins */

/* eslint-disable promise/catch-or-return */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { render } from 'react-dom';
import UIApplication from 'UIComponents/app/UIApplication'; // Redux

import { Provider } from 'react-redux';
import store from 'pricing-pages-unified-ui/utils/store';
import { storeEnvironmentConfig } from 'pricing-pages-unified-ui/actions/ConfigActions'; // Router

import { Router } from 'react-router-dom';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import rhumbConfig from '../rhumb-config.yaml';
import { createBrowserHistory } from 'history'; // Other

import Routes from './Routes'; // I18n

import createPublicI18nProvider from 'I18n/init/providers/createPublicI18nProvider';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import registerAllData from 'I18n/init/register/sync/registerAllData';
import 'I18n/init/data/currency';
import 'I18n/init/data/baseLocales';
import 'I18n/init/data/publicLocales'; // Other

import Raven from 'Raven';
import { setPrimaryTracker } from 'usage-tracker-container';
import ErrorPage from 'pricing-pages-unified-ui/components/ErrorPage';
import { getFormattedExperimentArray } from 'pricing-pages-unified-ui/utils/getFormattedExperimentArray';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import isThenable from 'hs-promise-utils/isThenable';
import { AllRoutes, MARKETING } from 'pricing-pages-lib/constants/PricingPageRoutes';
const {
  dispatch
} = store; // Actions for bootstrapping

import { fetchPurchaseMotionData } from 'pricing-pages-unified-ui/actions/PurchaseMotionActions';
import { fetchPublicProducts } from './actions/ProductsActions';
import { PublicI18nContext } from './context/I18nContext';
import { ScrollToTop } from './components/ScrollToTop';
import safeLocalStorage from 'pricing-pages-lib/utils/safeLocalStorage';
import { getAbsolutePricingPageUrl } from 'pricing-pages-lib/utils/urls';
import { fetchPublicMetaData } from './actions/public-only/PublicMetaDataActions';
import { Metrics } from './utils/metrics'; // const whenIdle = cb => {
//   if (window.hasOwnProperty('requestIdleCallback')) {
//     window.requestIdleCallback(cb);
//   } else {
//     setTimeout(cb, 0);
//   }
// };

export default class App {
  constructor(config) {
    this.config = config;
  } // Kicks off the app. This is intentionally not in the constructor.


  start() {
    setPrimaryTracker(this.config.tracker); // Set up I18n. Docs in: https://product.hubteam.com/docs/frontend/docs/i18n/new-i18n-setup.html

    let I18nProvider;

    if (window.I18N_MANUAL_LANG) {
      I18nProvider = createPublicI18nProvider({
        manuallySetLocale: true
      });
      I18nProvider.setLocale({
        locale: window.I18N_MANUAL_LANG,
        langEnabled: true
      });
    } else {
      I18nProvider = createPublicI18nProvider();
    }

    const langAvailable = Promise.all([I18nProvider.register(lang), registerAllData(I18nProvider)]);
    const history = createBrowserHistory({
      basename: this.config.rootUrl
    });
    const bootstrapBlockingPromises = this.fetchBootstrapBlockers(history);
    Promise.all([...bootstrapBlockingPromises, langAvailable]).then(() => {
      this.redirectNonProxied();
      this.startRouter(history, I18nProvider);
    }).catch(error => {
      langAvailable.then(() => {
        // We chain our bootstrapping code with this catch
        // so we can show an Error screen rather than just a blank page
        // A side effect of this is that any errors that bubble up here
        // would be swallowed up unless we re-throw.
        this.startRouter(history, I18nProvider, true);
        Raven.captureException(new Error('Failed to bootstrap public app'), {
          extra: {
            error
          }
        });
      });
    });
  }

  fetchBootstrapBlockers(history) {
    if (!history) {
      throw new Error(`cannot bootstrap without history`);
    }

    const promises = [];
    this.config.blockingRequests.forEach(blockingCall => {
      /*
       * Potential return value(s) of a blockingCall() include:
       *
       *   1. A promise
       *   2. A thunk action creator
       */
      let promise = blockingCall(); // Non-promises assumed to be thunk action creators

      if (!isThenable(promise)) {
        // Wrap in dispatch for thunk middleware to take effect
        promise = dispatch(promise);
      }

      promises.push(promise);
    });
    promises.push(dispatch(storeEnvironmentConfig(this.config)));
    return promises;
  }

  fetchPricingAndPurchasingData() {
    const promises = [];
    promises.push(dispatch(fetchPurchaseMotionData({
      getPurchaseMotions: this.config.getPurchaseMotions,
      isPublic: true
    })).catch(console.error));
    promises.push(dispatch(fetchPublicProducts()).catch(console.error));
    promises.push(dispatch(fetchPublicMetaData()).catch(console.error));
    return promises;
  }

  addChatTrackingEventHandler() {
    window.HubSpotConversations.on('conversationStarted', payload => {
      // Assumes experiment treatments are fetched by now. May not
      // be 100% correct for lazy loaded experiments, but probably
      // fine since this requires user interaction to fire and gives
      // enough time
      const formattedExperimentArray = getFormattedExperimentArray(store.getState().experimentTreatments);
      getUsageTracker().track('conversations', {
        action: 'conversation-started',
        formattedExperimentArray,
        conversationId: payload.conversation.conversationId
      });
      Metrics.counter('live-chat-interaction').increment();
    });
  }

  setupChatTracking() {
    if (window.HubSpotConversations) {
      this.addChatTrackingEventHandler();
      Metrics.counter('live-chat-loaded').increment();
    } else {
      window.hsConversationsOnReady = [() => {
        this.addChatTrackingEventHandler();
        Metrics.counter('live-chat-loaded').increment();
      }];
    }
  }

  redirectNonProxied() {
    const team = safeLocalStorage.getItem('hs-team'); // We do not support this proxy in QA (yet) so will not redirect in that environment

    const isOnApp = !!window.location.hostname.match(/^(app|app-eu1|local)\.hubspot(qa)?\.com/);
    const isQA = window.location.hostname.indexOf('qa') >= 0;
    const productLine = window.location.pathname.split('/').filter(_ => !!_).find(slug => {
      return AllRoutes.includes(slug);
    }) || MARKETING;
    const newUrl = `${getAbsolutePricingPageUrl({
      slug: productLine,
      isAppAuth: false,
      isQA
    })}${window.location.search}`;

    if (isOnApp && team !== 'cbx') {
      window.location = newUrl; // Commenting out for ATs until we get proper QA setup
      // Raven.captureException(
      //   new Error('Unexpected visitor on public app.hubspot.com')
      // );
    }
  }

  startRouter(history, i18nLoader, hasError = false) {
    this.fetchPricingAndPurchasingData();
    this.setupChatTracking();
    render( /*#__PURE__*/_jsx(UIApplication, {
      name: "pricing-pages-unified-ui",
      className: "public-pricing-page",
      children: /*#__PURE__*/_jsx(Provider, {
        store: store,
        children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
          value: {
            i18nLoader
          },
          children: /*#__PURE__*/_jsx(RhumbProvider, {
            captureExceptions: true,
            config: rhumbConfig,
            history: historyAdapter(history),
            children: /*#__PURE__*/_jsx(Router, {
              history: history,
              children: hasError ? /*#__PURE__*/_jsx(ErrorPage, {}) : /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(ScrollToTop, {}), /*#__PURE__*/_jsx(Routes, {
                  defaultRoute: {
                    search: history.location.search,
                    pathname: this.config.defaultHubRoute
                  }
                })]
              })
            })
          })
        })
      })
    }), document.getElementsByClassName('page')[0]);
  }

}